import React, { useState, useEffect } from 'react';
import { apiService, HeroSlide } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

const HeroSlides: React.FC = () => {
  const { showToast } = useToast();
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSlide, setEditingSlide] = useState<HeroSlide | null>(null);

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const data = await apiService.getHeroSlides();
      setSlides(data || []);
    } catch (error) {
      console.error('Failed to fetch slides:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);

    // Normalize boolean
    if (fd.has('is_active')) {
      const val = fd.get('is_active');
      fd.set('is_active', val === 'on' || val === 'true' ? 'true' : 'false');
    } else {
      fd.append('is_active', 'false');
    }

    try {
      if (editingSlide) {
        await apiService.updateHeroSlide(editingSlide.id, fd);
      } else {
        await apiService.createHeroSlide(fd);
      }

      setShowForm(false);
      setEditingSlide(null);
      showToast('Slide saved successfully!', 'success');
      fetchSlides();
    } catch (error: unknown) {
      console.error('Failed to save slide:', error);
      showToast('Failed to save slide.', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      try {
        await apiService.deleteHeroSlide(id);
        showToast('Slide deleted successfully!', 'success');
        fetchSlides();
      } catch (error) {
        console.error('Failed to delete slide:', error);
        showToast('Failed to delete slide.', 'error');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Hero Slides</h1>
          <p className="text-gray-600">Manage homepage banner carousel with multiple slides</p>
        </div>
        <button
          onClick={() => { setEditingSlide(null); setShowForm(true); }}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Slide
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900">
                {editingSlide ? 'Edit Slide' : 'New Slide'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input name="title" defaultValue={editingSlide?.title} required className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input name="subtitle" defaultValue={editingSlide?.subtitle} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle 2</label>
                    <input name="subtitle1" defaultValue={editingSlide?.subtitle1} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea name="description" defaultValue={editingSlide?.description} rows={2} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                      <input name="order" type="number" defaultValue={editingSlide?.order || 0} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <input type="checkbox" name="is_active" defaultChecked={editingSlide?.is_active !== false} className="h-5 w-5 text-orange-600 rounded" />
                      <label className="text-sm font-medium text-gray-700">Active</label>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Feature 1</label>
                    <input name="feature_text1" defaultValue={editingSlide?.feature_text1} required className="w-full border border-gray-200 rounded-lg px-4 py-2" placeholder="e.g. 24/7 Service" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Feature 2</label>
                    <input name="feature_text2" defaultValue={editingSlide?.feature_text2} required className="w-full border border-gray-200 rounded-lg px-4 py-2" placeholder="e.g. Expert Team" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Feature 3</label>
                    <input name="feature_text3" defaultValue={editingSlide?.feature_text3} required className="w-full border border-gray-200 rounded-lg px-4 py-2" placeholder="e.g. Quality Guaranteed" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image 1 (Main)</label>
                    {editingSlide?.background_image1 && (
                      <div className="mt-2 h-20 w-32 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                        <img src={getImageUrl(editingSlide.background_image1) || undefined} alt="" className="h-full w-full object-cover" />
                      </div>
                    )}
                    <input type="file" name="background_image1" accept="image/*" required={!editingSlide} className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image 2 (Optional)</label>
                    {editingSlide?.background_image2 && (
                      <div className="mt-2 h-20 w-32 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                        <img src={getImageUrl(editingSlide.background_image2) || undefined} alt="" className="h-full w-full object-cover" />
                      </div>
                    )}
                    <input type="file" name="background_image2" accept="image/*" className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image 3 (Optional)</label>
                    {editingSlide?.background_image3 && (
                      <div className="mt-2 h-20 w-32 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                        <img src={getImageUrl(editingSlide.background_image3) || undefined} alt="" className="h-full w-full object-cover" />
                      </div>
                    )}
                    <input type="file" name="background_image3" accept="image/*" className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-50">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 font-medium">Cancel</button>
                <button type="submit" className="px-8 py-2 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition">
                  {editingSlide ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {slides.map((slide) => (
          <div key={slide.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col md:flex-row group">
            <div className="md:w-2/5 relative bg-gray-100">
              <img src={getImageUrl(slide.background_image1) || undefined} alt={slide.title} className="w-full h-full object-cover" />
              <div className="absolute top-2 left-2 flex gap-1">
                {slide.background_image2 && <img src={getImageUrl(slide.background_image2) || undefined} className="w-6 h-6 rounded border border-white object-cover" alt="" />}
                {slide.background_image3 && <img src={getImageUrl(slide.background_image3) || undefined} className="w-6 h-6 rounded border border-white object-cover" alt="" />}
              </div>
            </div>
            <div className="p-6 flex-1">
              <div className="flex justify-between items-start mb-3">
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${slide.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {slide.is_active ? 'ACTIVE' : 'INACTIVE'}
                </span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => { setEditingSlide(slide); setShowForm(true); }} className="p-2 text-gray-400 hover:text-orange-600 bg-gray-50 rounded"><Edit className="h-4 w-4" /></button>
                  <button onClick={() => handleDelete(slide.id)} className="p-2 text-gray-400 hover:text-red-600 bg-gray-50 rounded"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 truncate">{slide.title}</h3>
              <p className="text-xs text-orange-600 font-semibold mt-0.5">{slide.subtitle}</p>
              <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-gray-50 text-[9px] uppercase tracking-wider text-gray-400 font-bold">
                <div>{slide.feature_text1}</div>
                <div>{slide.feature_text2}</div>
                <div>{slide.feature_text3}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {slides.length === 0 && (
        <div className="text-center py-24 bg-gray-50 rounded-xl border border-gray-100">
          <Eye className="mx-auto h-10 w-10 text-gray-300 mb-3" />
          <p className="text-gray-500">No hero slides found. Click "Add Slide" to start.</p>
        </div>
      )}
    </div>
  );
};

export default HeroSlides;